import { useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Message } from 'primereact/message'
import LayoutBuilderEditor from '../src/components/layoutBuilder/LayoutBuilderEditor'
import LayoutShareControls from '../src/components/layoutBuilder/LayoutShareControls'
import ImportLayoutDialog from '../src/components/layoutBuilder/ImportLayoutDialog'
import ImportLayoutPicker from '../src/components/layoutBuilder/ImportLayoutPicker'
import { downloadLayoutExport, exportBlocker, parseLayoutImport, INVALID_LAYOUT_FILE_MESSAGE } from '../src/components/layoutBuilder/layoutExport'
import LayoutHelpLink from '../src/components/layoutBuilder/LayoutHelpLink'
import { LAYOUT_BUILDER_SUBTITLE, LAYOUT_BUILDER_EMPTY_BLURB, IMPORT_LAYOUT_PROMPT, IMPORT_LAYOUT_LINK } from '../src/components/layoutBuilder/layoutBuilderCopy'
import { blankLayout, isMeaningfulLayout, decodeLayoutUuid } from '../src/components/layoutBuilder/layoutModel'
import useLayoutCatalogFromUrl from '../src/components/layoutBuilder/useLayoutCatalogFromUrl'
import useUnsavedChangesWarning from '../src/components/layoutBuilder/useUnsavedChangesWarning'
import validateEmail from '../src/utils/validations/validateEmail'
import { apiUrl, publicAppUrl, publicPrivacyPolicyUrl } from './publicConfig'
import {
  createPublicLayout,
  deletePublicLayout,
  deliverPublicLayoutLink,
  loadPublicLayout,
  updatePublicLayout
} from './publicLayoutApi'
import useClaimedMiss from './useClaimedMiss'
import ClaimedLayoutNotice from './ClaimedLayoutNotice'
import './publicLayoutBuilder.css'

const SAVE_DEBOUNCE_MS = 400
const ACCESS_WARNING = 'Anyone with this link can view, edit, or delete this layout. Share it only with people you trust.'
const COMPANY_HOME_URL = 'https://chain.io/'
const isValidRecipientEmail = value => {
  const trimmedEmail = value.trim()
  if (!trimmedEmail || validateEmail(trimmedEmail)) return false
  const nativeEmailInput = document.createElement('input')
  nativeEmailInput.type = 'email'
  nativeEmailInput.required = true
  nativeEmailInput.value = trimmedEmail
  const domainLabels = trimmedEmail.slice(trimmedEmail.lastIndexOf('@') + 1).split('.')
  const domainLabelsHaveValidBoundaries = domainLabels.every(label => !label.startsWith('-') && !label.endsWith('-'))
  return nativeEmailInput.checkValidity() && domainLabelsHaveValidBoundaries
}

const capabilityFromLocation = () => {
  const pathMatch = window.location.pathname.match(/^\/layouts\/([^/]+)\/?$/)
  if (!pathMatch) return null
  const layoutUuid = decodeLayoutUuid(pathMatch[1])
  return layoutUuid ? { layoutUuid } : null
}

const capabilityUrl = capability =>
  new URL(`/layouts/${encodeURIComponent(capability.layoutUuid)}`, publicAppUrl).toString()

function PublicLayoutBuilderPage () {
  const initialCapabilityRoute = useRef(capabilityFromLocation())
  const [capability, setCapability] = useState(initialCapabilityRoute.current)
  const [draft, setDraft] = useState(() => initialCapabilityRoute.current ? null : blankLayout())
  const [pageState, setPageState] = useState(
    initialCapabilityRoute.current ? 'loading' : 'ready'
  )
  const [saveState, setSaveState] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [retrySeconds, setRetrySeconds] = useState(0)
  const [retryAction, setRetryAction] = useState('save')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientEmailTouched, setRecipientEmailTouched] = useState(false)
  const [deliveryState, setDeliveryState] = useState('idle')
  const [deliveryRetrySeconds, setDeliveryRetrySeconds] = useState(0)
  const [emailDialogVisible, setEmailDialogVisible] = useState(false)
  const { claimed, explainLoadMiss, explainReadOnlyMiss, resetClaimed } = useClaimedMiss()
  // False on a fresh root visit (shows the first-run landing until "New layout" is
  // clicked); irrelevant once a capability exists, since a loaded/saved layout always
  // opens the editor directly.
  const [started, setStarted] = useState(false)
  const [importError, setImportError] = useState(null)
  const [importing, setImporting] = useState(false)
  // A parsed import file awaiting the visitor's confirmation (the public planner has no list of
  // existing layouts, so the name is only prefilled and editable - never de-duplicated).
  const [pendingImport, setPendingImport] = useState(null)
  const [importDialogError, setImportDialogError] = useState(null)
  const { catalog, loading: catalogLoading, error: catalogError, refetch: catalogRefetch } = useLayoutCatalogFromUrl(apiUrl)

  const capabilityRef = useRef(capability)
  capabilityRef.current = capability
  const draftRef = useRef(draft)
  draftRef.current = draft
  // Sticky across renders for BOTH the blank-unavailable and the read-only-unavailable states, so
  // handleChange/performSave stay gated (no further autosaves fire once the layout is unavailable).
  const isUnavailableState = pageState === 'unavailable' || pageState === 'unavailable-readonly'
  const unavailableRef = useRef(isUnavailableState)
  unavailableRef.current = isUnavailableState
  const saveTimerRef = useRef(null)
  const retryTimerRef = useRef(null)
  const deliveryRetryTimerRef = useRef(null)
  const blockedUntilRef = useRef(0)
  const blockedMessageRef = useRef(null)
  const saveQueueRef = useRef(Promise.resolve(true))
  const dirtyRef = useRef(false)
  const draftVersionRef = useRef(0)
  const createStartedRef = useRef(false)
  const loadRequestRef = useRef(0)
  const deleteInProgressRef = useRef(false)
  const importInProgressRef = useRef(false)
  // Bumped by handleNewLayout so a save/delivery in flight for a discarded draft is recognized
  // as stale (via isCurrentGeneration) and never applied to the blank draft that replaces it.
  // Its sole caller today (the first-run landing button) can't have in-flight work, so this is
  // currently defensive only; the header New layout action opens a new tab rather than resetting.
  const pageGenerationRef = useRef(0)
  const emailLinkButtonRef = useRef(null)
  const recipientEmailInputRef = useRef(null)

  const becomeUnavailable = () => {
    unavailableRef.current = true
    dirtyRef.current = false
    setPageState('unavailable')
    setDraft(null)
    setSaveState(null)
    setSaveError(null)
  }

  // A benign null from an update/delete (e.g. the post-read races the backend describes) must not
  // destroy the visitor's anonymous work. Keep the current draft and show it read-only with a notice,
  // so copy-link/email stay available instead of blanking to "deleted or unavailable".
  // Saving stops here, synchronously; only then is the claim status asked about.
  const becomeReadOnlyUnavailable = missedCapability => {
    unavailableRef.current = true
    dirtyRef.current = false
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    setPageState('unavailable-readonly')
    setSaveState(null)
    setSaveError(null)
    const generation = pageGenerationRef.current
    explainReadOnlyMiss(missedCapability.layoutUuid, () => pageGenerationRef.current === generation && unavailableRef.current)
  }

  const beginRetryWindow = seconds => {
    if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current)
    if (!seconds) {
      blockedUntilRef.current = 0
      blockedMessageRef.current = null
      setRetrySeconds(0)
      return
    }
    blockedUntilRef.current = Date.now() + (seconds * 1000)
    setRetrySeconds(seconds)
    retryTimerRef.current = window.setTimeout(() => {
      blockedUntilRef.current = 0
      setRetrySeconds(0)
    }, seconds * 1000)
  }

  const beginDeliveryRetryWindow = seconds => {
    if (deliveryRetryTimerRef.current) window.clearTimeout(deliveryRetryTimerRef.current)
    if (!seconds) {
      setDeliveryRetrySeconds(0)
      return
    }
    setDeliveryRetrySeconds(seconds)
    deliveryRetryTimerRef.current = window.setTimeout(() => {
      setDeliveryRetrySeconds(0)
    }, seconds * 1000)
  }

  const performSave = async (layout, version) => {
    if (unavailableRef.current || Date.now() < blockedUntilRef.current) return false
    const generation = pageGenerationRef.current
    const isCurrentGeneration = () => pageGenerationRef.current === generation
    setSaveState('saving')
    setSaveError(null)
    try {
      const currentCapability = capabilityRef.current
      if (!currentCapability) {
        // Recheck at the moment the create would actually fire: if the draft was cleared to a
        // non-meaningful layout while this save sat queued behind an earlier (e.g. failed) create,
        // don't create an empty layout. Clear the latch so a later meaningful edit can create fresh.
        if (!isMeaningfulLayout(layout) || !isMeaningfulLayout(draftRef.current)) {
          createStartedRef.current = false
          if (draftVersionRef.current === version) {
            dirtyRef.current = false
            setSaveState(null)
            setSaveError(null)
          }
          return false
        }
        createStartedRef.current = true
        const created = await createPublicLayout(layout)
        if (!isCurrentGeneration()) return false
        if (!created?.layout_uuid) {
          throw new Error('Could not confirm this layout was saved. Your draft is still here. Please retry saving.')
        }
        const nextCapability = { layoutUuid: created.layout_uuid }
        capabilityRef.current = nextCapability
        setCapability(nextCapability)
        window.history.replaceState({}, '', `/layouts/${encodeURIComponent(nextCapability.layoutUuid)}`)
        const returnedDocumentMatches = created.name === layout.name &&
          _.isEqual(created.integrations, layout.integrations)
        if (!returnedDocumentMatches) {
          const updated = await updatePublicLayout(nextCapability, layout)
          if (!isCurrentGeneration()) return false
          if (!updated) {
            becomeReadOnlyUnavailable(nextCapability)
            return false
          }
        }
      } else {
        const updated = await updatePublicLayout(currentCapability, layout)
        if (!isCurrentGeneration()) return false
        if (!updated) {
          becomeReadOnlyUnavailable(currentCapability)
          return false
        }
      }
      if (draftVersionRef.current === version) {
        dirtyRef.current = false
        setSaveState('saved')
      }
      beginRetryWindow(0)
      return true
    } catch (error) {
      if (!isCurrentGeneration()) return false
      const wait = error.retryAfter || 0
      // Reset the create-on-first-edit latch on ANY create failure (not just rate-limits): a network
      // error or 500 has no retryAfter, and leaving the latch set would block every later create for
      // the rest of the session.
      if (!capabilityRef.current) createStartedRef.current = false
      const message = wait ? `${error.message} Try again in ${wait} seconds.` : error.message
      if (wait) blockedMessageRef.current = message
      beginRetryWindow(wait)
      setRetryAction('save')
      setSaveState('error')
      setSaveError(message)
      return false
    }
  }

  const queueSave = (layout, version = draftVersionRef.current) => {
    const pending = saveQueueRef.current.then(() => performSave(layout, version))
    saveQueueRef.current = pending
    return pending
  }

  const scheduleSave = (layout, version) => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null
      queueSave(layout, version)
    }, SAVE_DEBOUNCE_MS)
  }

  const loadCapability = async () => {
    const initialCapability = initialCapabilityRoute.current
    if (!initialCapability) return
    const requestId = ++loadRequestRef.current
    setPageState('loading')
    try {
      const layout = await loadPublicLayout(initialCapability.layoutUuid)
      if (loadRequestRef.current !== requestId) return
      if (!layout) {
        // Stays 'loading' while asking whether the layout was claimed.
        if (!(await explainLoadMiss(initialCapability.layoutUuid, () => loadRequestRef.current === requestId))) return
        becomeUnavailable()
        return
      }
      setDraft({ ...layout, integrations: Array.isArray(layout.integrations) ? layout.integrations : [] })
      setPageState('ready')
    } catch {
      if (loadRequestRef.current === requestId) setPageState('load-error')
    }
  }

  useEffect(() => {
    loadCapability()
    return () => { loadRequestRef.current += 1 }
  }, [])

  useUnsavedChangesWarning(dirtyRef)

  useEffect(() => () => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
    if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current)
    if (deliveryRetryTimerRef.current) window.clearTimeout(deliveryRetryTimerRef.current)
  }, [])

  const handleChange = nextLayout => {
    if (unavailableRef.current || deleteInProgressRef.current) return
    setDraft(nextLayout)
    if (!capabilityRef.current && !createStartedRef.current && !isMeaningfulLayout(nextLayout)) {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      dirtyRef.current = false
      setSaveState(null)
      setSaveError(null)
      return
    }
    draftVersionRef.current += 1
    dirtyRef.current = true
    if (Date.now() < blockedUntilRef.current) {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      setRetryAction('save')
      setSaveState('error')
      setSaveError(blockedMessageRef.current)
      return
    }
    setSaveState('saving')
    setSaveError(null)
    scheduleSave(nextLayout, draftVersionRef.current)
  }

  const handleDelete = async () => {
    if (unavailableRef.current || deleteInProgressRef.current) return
    deleteInProgressRef.current = true
    setPageState('deleting')
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
      const saved = await queueSave(draftRef.current, draftVersionRef.current)
      if (!saved) {
        deleteInProgressRef.current = false
        if (!unavailableRef.current) setPageState('ready')
        return
      }
    } else if (!(await saveQueueRef.current)) {
      deleteInProgressRef.current = false
      // Stay read-only once the layout is unavailable; only restore the editable view when the
      // awaited save left the layout still available.
      if (!unavailableRef.current) setPageState('ready')
      return
    }
    const currentCapability = capabilityRef.current
    if (!currentCapability || Date.now() < blockedUntilRef.current) {
      deleteInProgressRef.current = false
      setPageState('ready')
      return
    }
    setSaveError(null)
    try {
      const deleted = await deletePublicLayout(currentCapability)
      if (!deleted) {
        // A delete that didn't confirm shouldn't wipe the visitor's work either — keep it read-only.
        becomeReadOnlyUnavailable(currentCapability)
        return
      }
      becomeUnavailable()
    } catch (error) {
      const wait = error.retryAfter || 0
      const message = wait ? `${error.message} Try again in ${wait} seconds.` : error.message
      if (wait) blockedMessageRef.current = message
      beginRetryWindow(wait)
      setRetryAction('delete')
      setSaveError(message)
      deleteInProgressRef.current = false
      setPageState('ready')
    }
  }

  const handleRetry = () => {
    if (!draftRef.current || Date.now() < blockedUntilRef.current) return
    if (retryAction === 'delete') {
      handleDelete()
      return
    }
    queueSave(draftRef.current, draftVersionRef.current)
  }

  // Starts a fresh blank layout from the first-run landing button. (The header's New layout
  // action opens a fresh planner in a new tab instead, so it does not call this.) The only
  // caller therefore runs before any capability exists, i.e. before a save/delivery can be in
  // flight - so the pageGenerationRef bump below is a defensive no-op in practice today, kept
  // so a future in-place caller would still invalidate stale in-flight work. Never calls the
  // API itself - create-on-first-edit still governs when a new record is actually created.
  const handleNewLayout = () => {
    pageGenerationRef.current += 1
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    beginRetryWindow(0)
    beginDeliveryRetryWindow(0)
    loadRequestRef.current += 1
    unavailableRef.current = false
    dirtyRef.current = false
    draftVersionRef.current = 0
    createStartedRef.current = false
    deleteInProgressRef.current = false
    saveQueueRef.current = Promise.resolve(true)
    capabilityRef.current = null
    window.history.replaceState({}, '', '/')
    setCapability(null)
    setDraft(blankLayout())
    setPageState('ready')
    resetClaimed()
    setSaveState(null)
    setSaveError(null)
    setDeliveryState('idle')
    setEmailDialogVisible(false)
    setRecipientEmail('')
    setRecipientEmailTouched(false)
    setStarted(true)
  }

  // Reads a layout export file chosen on the first-run landing and asks the visitor to confirm it.
  // A bad file shows a friendly message, opens no dialog and never calls create.
  const handleImport = async file => {
    if (importInProgressRef.current) return
    setImportError(null)
    let parsed
    try {
      parsed = parseLayoutImport(await file.text())
    } catch {
      parsed = { error: INVALID_LAYOUT_FILE_MESSAGE }
    }
    if (parsed.error) {
      setImportError(parsed.error)
      return
    }
    setImportDialogError(null)
    setPendingImport(parsed.layout)
  }

  // Creates a NEW layout from the confirmed import, then opens it the way a first-saved layout opens
  // (capability + /layouts/<uuid>).
  const confirmImport = async name => {
    if (importInProgressRef.current) return
    const imported = { ...pendingImport, name }
    importInProgressRef.current = true
    setImporting(true)
    setImportError(null)
    setImportDialogError(null)
    let opened = false
    try {
      const created = await createPublicLayout(imported)
      if (!created?.layout_uuid) {
        throw new Error('Could not confirm this layout was saved. Please try importing again.')
      }
      const nextCapability = { layoutUuid: created.layout_uuid }
      const returnedDocumentMatches = created.name === imported.name &&
        _.isEqual(created.integrations, imported.integrations)
      capabilityRef.current = nextCapability
      createStartedRef.current = true
      setCapability(nextCapability)
      setDraft(returnedDocumentMatches ? created : imported)
      setStarted(true)
      opened = true
      setPendingImport(null)
      window.history.replaceState({}, '', `/layouts/${encodeURIComponent(nextCapability.layoutUuid)}`)
      if (returnedDocumentMatches) {
        setSaveState('saved')
      } else {
        // The editor is already open: the follow-up goes through the save queue, so an edit made
        // meanwhile is saved after it and is never overwritten by the older imported content.
        draftVersionRef.current += 1
        dirtyRef.current = true
        setSaveState('saving')
        queueSave(imported, draftVersionRef.current)
      }
    } catch (error) {
      const wait = error.retryAfter || 0
      const message = wait ? `${error.message} Try again in ${wait} seconds.` : error.message
      if (wait) blockedMessageRef.current = message
      beginRetryWindow(wait)
      if (opened) {
        // The layout exists but its content did not finish saving: keep it open with the usual retry.
        draftVersionRef.current += 1
        dirtyRef.current = true
        setRetryAction('save')
        setSaveState('error')
        setSaveError(message)
      } else {
        // Nothing was created: keep the dialog open to retry or cancel, with the error shown in it.
        setImportDialogError(message)
      }
    } finally {
      importInProgressRef.current = false
      setImporting(false)
    }
  }

  const handleDelivery = async event => {
    event?.preventDefault()
    const currentCapability = capabilityRef.current
    if (!currentCapability || deliveryState === 'sending' || deliveryRetrySeconds > 0) return
    const trimmedEmail = recipientEmail.trim()
    if (!isValidRecipientEmail(trimmedEmail)) {
      setRecipientEmailTouched(true)
      return
    }
    const generation = pageGenerationRef.current
    const isCurrentGeneration = () => pageGenerationRef.current === generation
    setDeliveryState('sending')
    try {
      const result = await deliverPublicLayoutLink(currentCapability, trimmedEmail)
      if (!isCurrentGeneration()) return
      if (result?.email_sent) {
        setDeliveryState('sent')
        closeEmailDialog()
        return
      }
      setDeliveryState('failed')
    } catch (error) {
      if (!isCurrentGeneration()) return
      if (error.code === 'RATE_LIMITED') {
        beginDeliveryRetryWindow(error.retryAfter || 0)
        setDeliveryState('rate-limited')
        return
      }
      setDeliveryState('failed')
    }
  }

  const handleRecipientEmailChange = event => {
    const nextEmail = event.target.value
    if (nextEmail !== recipientEmail) {
      setDeliveryState('idle')
      beginDeliveryRetryWindow(0)
    }
    setRecipientEmail(nextEmail)
  }

  const recipientEmailValid = isValidRecipientEmail(recipientEmail)
  const showRecipientEmailError = recipientEmailTouched && !recipientEmailValid

  const link = capability ? capabilityUrl(capability) : null

  const closeEmailDialog = () => {
    setEmailDialogVisible(false)
    window.setTimeout(() => emailLinkButtonRef.current?.focus(), 0)
  }

  const newLayoutButton = (
    <Button
      label='New layout'
      icon='pi pi-plus'
      outlined
      size='small'
      // Opens a fresh planner in a new tab so the current layout stays open here,
      // matching how the external links preserve unsaved work in this tab.
      onClick={() => window.open(publicAppUrl, '_blank', 'noopener,noreferrer')}
    />
  )
  // Once claimed, the public link no longer opens the layout, so Copy/Email link are dead.
  const claimedReadOnly = pageState === 'unavailable-readonly' && claimed
  let titleActions = null
  if (link && claimedReadOnly) {
    titleActions = <div className='publicLayoutBuilderPage-share-actions'>{newLayoutButton}</div>
  } else if (link) {
    titleActions = (
      <LayoutShareControls
        link={link}
        accessWarning={ACCESS_WARNING}
        popoverClassName='publicLayoutBuilderPage-copy-popover'
        containerClassName='publicLayoutBuilderPage-share-actions'
        leadingActions={newLayoutButton}
        onEmailLink={() => setEmailDialogVisible(true)}
        emailButtonRef={emailLinkButtonRef}
        onExport={() => downloadLayoutExport(draft)}
        exportBlocker={exportBlocker(draft)}
      />
    )
  }

  return (
    <div className='publicLayoutBuilderPage'>
      <header className='publicLayoutBuilderPage-header'>
        <a
          className='publicLayoutBuilderPage-brand'
          href={COMPANY_HOME_URL}
          target='_blank'
          rel='noopener noreferrer'
        >
          Chain.io
        </a>
        <a className='publicLayoutBuilderPage-title' href='/'>Layout Planner</a>
        <p className='layoutBuilder-subtitle'>{LAYOUT_BUILDER_SUBTITLE}</p>
      </header>
      <main className='publicLayoutBuilderPage-main'>
        {pageState === 'loading' && <p role='status'>Loading layout…</p>}
        {pageState === 'load-error' && (
          <section className='publicLayoutBuilderPage-unavailable'>
            <h1>Could not load this layout</h1>
            <p>Check your connection and try again.</p>
            <Button label='Retry loading' onClick={loadCapability} />
          </section>
        )}
        {pageState === 'deleting' && <p role='status'>Deleting layout…</p>}
        {pageState === 'unavailable' && claimed && capability && <ClaimedLayoutNotice layoutUuid={capability.layoutUuid} />}
        {pageState === 'unavailable' && !claimed && (
          <section className='publicLayoutBuilderPage-unavailable'>
            <h1>Layout deleted or unavailable</h1>
            <p>This capability link cannot open a public layout.</p>
          </section>
        )}
        {pageState === 'ready' && draft && !capability && !started && (
          <section className='layoutBuilder-landing-empty'>
            <div className='layoutBuilder-landing-empty-content'>
              <div className='layoutBuilder-layout-illustration' role='img' aria-label='Three stacked layout sheets'>
                <span /><span /><span />
              </div>
              <div>
                <h1>Plan your layout</h1>
                <p className='layoutBuilder-empty-blurb'>{LAYOUT_BUILDER_EMPTY_BLURB}</p>
                <p><LayoutHelpLink /></p>
              </div>
              <Button label='New layout' icon='pi pi-plus' disabled={importing} onClick={handleNewLayout} />
              <p className='publicLayoutBuilderPage-import'>
                {IMPORT_LAYOUT_PROMPT}{' '}
                <ImportLayoutPicker onImport={handleImport}>
                  {open => <Button label={IMPORT_LAYOUT_LINK} link disabled={importing} onClick={open} />}
                </ImportLayoutPicker>
              </p>
              {importError && <Message severity='error' text={importError} />}
              <ImportLayoutDialog
                layout={pendingImport}
                initialName={pendingImport?.name}
                busy={importing}
                error={importDialogError}
                onConfirm={confirmImport}
                onCancel={() => setPendingImport(null)}
              />
            </div>
          </section>
        )}
        {(pageState === 'ready' || pageState === 'unavailable-readonly') && draft && (capability || started) && (
          <>
            {pageState === 'unavailable-readonly' && claimed && capability && (
              <ClaimedLayoutNotice layoutUuid={capability.layoutUuid} variant='banner' />
            )}
            {pageState === 'unavailable-readonly' && !claimed && (
              <Message
                severity='warn'
                className='publicLayoutBuilderPage-readonly-notice'
                text='This layout is no longer available to edit. Copying or emailing the link shares the last saved version, which may not include your most recent changes.'
              />
            )}
            <LayoutBuilderEditor
              layout={draft}
              catalog={catalog}
              catalogLoading={catalogLoading}
              catalogError={catalogError}
              catalogRefetch={catalogRefetch}
              readOnly={pageState === 'unavailable-readonly'}
              isNew={!capability}
              saveState={saveState}
              titleActions={titleActions}
              onChange={handleChange}
              onDelete={capability && pageState !== 'unavailable-readonly' ? handleDelete : undefined}
            />
            <Dialog
              header='Email layout link'
              visible={emailDialogVisible}
              modal
              focusOnShow={false}
              className='publicLayoutBuilderPage-email-dialog'
              onShow={() => window.setTimeout(() => recipientEmailInputRef.current?.focus(), 0)}
              onHide={closeEmailDialog}
            >
              <div className='publicLayoutBuilderPage-delivery'>
                <Message severity='warn' text={ACCESS_WARNING} />
                <p>Send this link to yourself or someone else. By requesting delivery, you agree to our <a href={publicPrivacyPolicyUrl} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>.</p>
                <form onSubmit={handleDelivery}>
                  <label htmlFor='public-layout-recipient-email'>Recipient email</label>
                  <input
                    id='public-layout-recipient-email'
                    ref={recipientEmailInputRef}
                    type='email'
                    required
                    value={recipientEmail}
                    onChange={handleRecipientEmailChange}
                    onBlur={() => setRecipientEmailTouched(true)}
                    autoFocus
                    disabled={deliveryState === 'sending'}
                    aria-invalid={showRecipientEmailError || undefined}
                    aria-describedby={showRecipientEmailError ? 'public-layout-recipient-email-error' : undefined}
                  />
                  <Button
                    type='submit'
                    label={deliveryState === 'sending' ? 'Sending…' : 'Send link'}
                    disabled={!recipientEmail || !recipientEmailValid || deliveryState === 'sending' || deliveryRetrySeconds > 0}
                  />
                  {showRecipientEmailError && (
                    <p id='public-layout-recipient-email-error' role='alert' className='publicLayoutBuilderPage-field-error'>
                      Enter a valid email address.
                    </p>
                  )}
                </form>
                {deliveryState === 'sending' && <p role='status'>Sending email…</p>}
                {deliveryState === 'sent' && <p role='status'>Email sent.</p>}
                {deliveryState === 'failed' && (
                  <div className='publicLayoutBuilderPage-delivery-status' role='status'>
                    <p>Email not sent. Your saved link is still available from Copy link.</p>
                    <Button label='Retry email' onClick={handleDelivery} disabled={deliveryRetrySeconds > 0} />
                  </div>
                )}
                {deliveryState === 'rate-limited' && (
                  <div className='publicLayoutBuilderPage-delivery-status' role='status'>
                    <p>Email delivery is temporarily rate limited. Try again in {deliveryRetrySeconds} seconds.</p>
                    <Button label='Retry email' onClick={handleDelivery} disabled={deliveryRetrySeconds > 0} />
                  </div>
                )}
              </div>
            </Dialog>
            {deliveryState === 'sent' && !emailDialogVisible && <p role='status'>Email sent.</p>}
            {catalogError && <Message severity='warn' text={`Integration catalog failed to load: ${catalogError.message}`} />}
            {saveError && (
              <div className='publicLayoutBuilderPage-save-error'>
                <Message severity='error' text={saveError} />
                <Button
                  label={retryAction === 'delete' ? 'Retry delete' : 'Retry save'}
                  disabled={retrySeconds > 0}
                  onClick={handleRetry}
                />
              </div>
            )}
          </>
        )}
      </main>
      <footer className='publicLayoutBuilderPage-footer'>
        <a href={COMPANY_HOME_URL} target='_blank' rel='noopener noreferrer'>Chain.io</a>
        <a href={publicPrivacyPolicyUrl} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>
      </footer>
    </div>
  )
}

export default PublicLayoutBuilderPage
